import React from 'react';

// Masonry layout using CSS columns (fluid, tightly packed, no crop)
export default function MasonryGallery({
  photos = [],
  gap = 8,
  minColumnWidth = 320,
  maxColumns = Infinity,
  shareToken = null,
  watermark = false,
  onPhotoClick = () => {},
}) {
  const containerRef = React.useRef(null);
  const [width, setWidth] = React.useState(0);
  const [naturalOrientation, setNaturalOrientation] = React.useState({});

  const updateOrientation = React.useCallback((id, orientation) => {
    if (!id) return;
    setNaturalOrientation((prev) => (prev[id] === orientation ? prev : { ...prev, [id]: orientation }));
  }, []);

  const resolveOrientation = React.useCallback(
    (img) => {
      const fromNatural = naturalOrientation[img.id];
      if (fromNatural) return fromNatural;
      const w = Number(img.width) || 0;
      const h = Number(img.height) || 0;
      if (w > 0 && h > 0) return w >= h ? 'landscape' : 'portrait';
      if (img.orientation === 'portrait') return 'portrait';
      if (img.orientation === 'landscape') return 'landscape';
      if (img.orientation === 'square') return 'square';
      return 'landscape';
    },
    [naturalOrientation]
  );

  React.useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        setWidth((prev) => (prev !== w ? w : prev));
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const columns = React.useMemo(() => {
    if (!width) return 1;
    const computed = Math.max(1, Math.floor((width + gap) / (minColumnWidth + gap)));
    return Math.max(1, Math.min(computed, Number.isFinite(maxColumns) ? maxColumns : computed));
  }, [width, gap, minColumnWidth, maxColumns]);

  const colWidth = React.useMemo(() => {
    if (!width || columns <= 0) return 0;
    return (width - gap * (columns - 1)) / columns;
  }, [width, columns, gap]);

  const variant = React.useMemo(() => {
    if (!colWidth) return 'md';
    if (colWidth <= 240) return 'sm';
    if (colWidth <= 480) return 'md';
    return 'lg';
  }, [colWidth]);

  const srcFor = React.useCallback(
    (img) => {
      const base = img.filename.replace(/\.[^.]+$/, '.jpg');
      if (shareToken) return `/media/share/${variant}/${base}?token=${shareToken}`;
      const t = window?.localStorage?.getItem('auth_token');
      const q = '';
      if (watermark) return `/media/watermarked/${variant}/${base}${q}`;
      return `/media/variants/${variant}/${base}${q}`;
    },
    [shareToken, variant, watermark]
  );

  // Balanced layout: assign each photo to shortest column
  const layout = React.useMemo(() => {
    if (!width || columns <= 0) return { columns: [], colWidth: 0 };
    const colWidth = (width - gap * (columns - 1)) / columns;
    const cols = Array.from({ length: columns }, () => ({ height: 0, items: [] }));
    const aspect = (p) => {
      const w = Number(p.width) || 0;
      const h = Number(p.height) || 0;
      if (w > 0 && h > 0) return w / h;
      if (p.orientation === 'portrait') return 2 / 3;
      if (p.orientation === 'square') return 1;
      return 3 / 2;
    };
    photos.forEach((img, index) => {
      const a = aspect(img);
      const h = a > 0 ? Math.round(colWidth / a) : Math.round(colWidth);
      // find shortest column
      let minIdx = 0;
      let min = cols[0].height;
      for (let i = 1; i < cols.length; i++) {
        if (cols[i].height < min) { min = cols[i].height; minIdx = i; }
      }
      cols[minIdx].items.push({ img, index, height: h });
      cols[minIdx].height += h + gap;
    });
    return { columns: cols, colWidth };
  }, [photos, width, columns, gap]);

  return (
    <div ref={containerRef} className="masonry-gallery" style={{ display: 'flex', gap }}>
      {layout.columns.map((col, colIdx) => (
        <div key={colIdx} style={{ flex: `0 0 ${(width - gap * (columns - 1)) / columns}px`, display: 'flex', flexDirection: 'column', gap }}>
          {col.items.map(({ img, index }) => (
            <div
              key={img.id}
              data-image-id={img.id}
              className={`masonry-item ${resolveOrientation(img)}`}
            >
              <img
                src={srcFor(img)}
                alt={img.title || 'Photo'}
                loading="lazy"
                onClick={() => onPhotoClick(img, index)}
                onLoad={(event) => {
                  const { naturalWidth, naturalHeight } = event.currentTarget;
                  if (!naturalWidth || !naturalHeight) return;
                  const ratio = naturalWidth / naturalHeight;
                  if (Math.abs(ratio - 1) < 0.04) {
                    updateOrientation(img.id, 'square');
                  } else {
                    updateOrientation(img.id, ratio >= 1 ? 'landscape' : 'portrait');
                  }
                }}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
